import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import CloseIcon from '@mui/icons-material/Close'
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined'

import { theme } from '../../themes/theme'
import { FlexColumn, FlexRow } from '../styles/Containers'
import { SecondaryButton, SecondaryIconButton } from '../common/Button'
import { ErrorText } from '../styles/Text'
import { PANEL_BORDER, SECONDARY_TEXT } from '../styles/colors'

const PDFContainer = styled(FlexColumn)`
  position: sticky;
  top: 20px;
  gap: 0;
  height: max-content;
  min-width: 350px;
  max-width: 900px;
  flex: 1 1 460px;
  border: 1px solid ${PANEL_BORDER};
  border-radius: 8px;
  overflow: hidden;
  background: ${theme.colors.background.white};
`

const PreviewHeader = styled(FlexRow)`
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
  padding: 8px 8px 8px 14px;
  border-bottom: 1px solid ${PANEL_BORDER};
  background: ${theme.colors.background.light};
`

const PreviewName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${SECONDARY_TEXT};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const JumpButton = styled(SecondaryButton)`
  flex: 0 0 auto;
  white-space: nowrap;
`

const Pdf = styled.iframe`
  border: none;
  min-height: 700px;
`

// In-tab PDF preview with optional search-match navigation.
function InlineFilePreview({ preview }) {
  const { activeFile, pdfUrl, error, pdfFragment, matchPage, previewRef, viewerKey, jumpToMatch, close } = preview
  if (error) {
    return (
      <PDFContainer ref={previewRef} data-testid="inline-file-preview">
        <PreviewHeader>
          <PreviewName title={error.filename ?? undefined}>{error.filename ?? 'File'}</PreviewName>
          <SecondaryIconButton onClick={close} aria-label="Close PDF viewer">
            <CloseIcon />
          </SecondaryIconButton>
        </PreviewHeader>
        <ErrorText role="alert" data-testid="inline-file-preview-error" style={{ padding: '18px 14px' }}>
          This file could not be opened for preview. Try again, or download it instead.
        </ErrorText>
      </PDFContainer>
    )
  }
  if (!activeFile || !pdfUrl) return null
  return (
    <PDFContainer ref={previewRef} data-testid="inline-file-preview">
      <PreviewHeader>
        <PreviewName title={activeFile.filename}>{activeFile.filename}</PreviewName>
        {matchPage && (
          <JumpButton
            type="button"
            onClick={jumpToMatch}
            padding="short"
            aria-label={`Jump to the match on page ${matchPage}`}
            title={`Jump to the match on page ${matchPage}`}
            data-testid="jump-to-match">
            <MyLocationOutlinedIcon /> match on p. {matchPage}
          </JumpButton>
        )}
        <SecondaryIconButton onClick={close} aria-label="Close PDF viewer">
          <CloseIcon />
        </SecondaryIconButton>
      </PreviewHeader>
      <Pdf key={viewerKey} src={`${pdfUrl}${pdfFragment}`} title="File preview" height="100%" width="100%" type="application/pdf" />
    </PDFContainer>
  )
}

InlineFilePreview.propTypes = { preview: PropTypes.object.isRequired }

export default InlineFilePreview
